import type { ReactNode } from "react";
import clsx from "clsx";

export function Page({ title, sub, actions, children }: { title: string; sub?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="p-6 max-w-[1280px]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="app-title">{title}</h1>
          {sub && <p className="app-sub">{sub}</p>}
        </div>
        {actions && <div className="flex gap-2 shrink-0">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export type Tone = "high" | "elevated" | "good" | "neutral";

export function Chip({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return <span className={clsx("chip", tone)}>{children}</span>;
}

export function statusTone(status: string | null | undefined): Tone {
  switch ((status ?? "").toLowerCase()) {
    case "active":
    case "completed":
    case "on_track":
    case "merged":
    case "won":
    case "resolved":
    case "closed":
    case "good":
      return "good";
    case "degraded":
    case "at_risk":
    case "missed":
    case "awaiting_approval":
    case "pending":
    case "elevated":
      return "elevated";
    case "revoked":
    case "off_track":
    case "lost":
    case "failed":
    case "high":
    case "overdue":
      return "high";
    default:
      return "neutral";
  }
}

export function Kpi({ label, value, unit, tone, delta, hint }: { label: string; value: ReactNode; unit?: string; tone?: Tone; delta?: ReactNode; hint?: string }) {
  return (
    <div className="card kpi" title={hint}>
      <h4>{label}</h4>
      <div className="v">
        <span>
          {value}
          {unit && <span className="text-[13px] text-muted font-semibold ml-1">{unit}</span>}
        </span>
        {tone && <Chip tone={tone}>{tone === "good" ? "Good" : tone === "elevated" ? "Elevated" : tone === "high" ? "High" : "—"}</Chip>}
      </div>
      {delta && <div className="delta">{delta}</div>}
    </div>
  );
}

export function Button({ variant = "dark", className, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "dark" | "light" }) {
  return <button className={clsx("btn", variant === "dark" ? "btn-dark" : "btn-light", className)} {...rest} />;
}

export function Empty({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="card text-center py-10">
      <div className="font-bold text-[15px]">{title}</div>
      {body && <p className="text-muted text-[13px] mt-1 max-w-[52ch] mx-auto">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : String(error);
  return <div className="card border-[color:var(--status-high)] text-[13px]">Something went wrong: {msg}</div>;
}

export function Loading() {
  return <div className="text-muted text-[13px] py-6">Loading…</div>;
}

export function Table<T>({ rows, cols, keyOf, empty }: { rows: T[]; cols: { h: string; cell: (r: T) => ReactNode; w?: string }[]; keyOf: (r: T) => string; empty?: string }) {
  if (rows.length === 0 && empty) return <Empty title={empty} />;
  return (
    <div className="overflow-x-auto">
      <table className="tbl">
        <thead>
          <tr>{cols.map((c) => <th key={c.h} style={c.w ? { width: c.w } : undefined}>{c.h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={keyOf(r)}>{cols.map((c) => <td key={c.h}>{c.cell(r)}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-[12px] font-bold text-text-4 uppercase tracking-wide">
      {label}
      <div className="mt-1 normal-case tracking-normal font-normal">{children}</div>
    </label>
  );
}

/** Style-guide pill tabs: one job per pane. `count` is an optional badge on a tab. */
export function Tabs<K extends string>({ tabs, active, onChange }: { tabs: { key: K; label: string; count?: number }[]; active: K; onChange: (k: K) => void }) {
  return (
    <div className="tabs mb-5" role="tablist">
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={t.key === active} className={clsx("tab", t.key === active && "active")} onClick={() => onChange(t.key)}>
          {t.label}
          {t.count != null && t.count > 0 && <span className="count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}
